import z from 'zod';
import api from '../../api';
import models from '../../../models';
import { createCallToolResult, createTextContent } from '../output-parser';
import type { WrappedRegisterToolFuncType } from '../tool-definition';

const name = 'fetch-teleport-context';

const inputSchema = models.tool.fetchTeleportContextInput;

const config = {
  title: 'Fetch Teleport Context',
  description:
    'Fetch the full context Wild Moose holds for an alert investigation: the enrichment it produced when the alert fired - the results of the automated investigation - together with the discussion around it. This retrieves existing context - it does not trigger a new investigation.\n\n' +
    'Call this at the start of an alert or incident investigation, before gathering data yourself, so the session builds on what Wild Moose already found. Identify it with exactly one of `enrichmentLink` (the enrichment message itself) or `alertLink` (the alert it was produced for) — pass one, never both. Prefer `enrichmentLink` when you have it: it names the enrichment directly, and it is the only way to reach one posted to a dedicated enrichment channel rather than the alert channel. In a handoff session, use whichever link the handoff context carries, as-is.\n\n' +
    'Returns a JSON object holding everything Wild Moose has for this investigation - what it found, the data behind it, and the surrounding chat. Read the payload and work from what is in it. The set of fields is owned by the server and grows over time, and parts that do not apply to a given client or run are simply absent, so do not assume a fixed shape. A missing part is a reporting mode, not a failure: never describe it to the user as an empty or broken enrichment, and do not speculate about why it is not there.\n\n' +
    'Fails with a not-found error when the enrichment cannot be found (e.g. Wild Moose is still investigating, or the link does not point to an alert Wild Moose processed or to an enrichment message). Fails with a forbidden error when the message you named lives in a channel you are not a member of.',
  inputSchema,
};

const handler = async (input: z.infer<typeof config.inputSchema>) => {
  const result = await api.fetchTeleportContext(input);
  return createCallToolResult({
    contentBlocks: [createTextContent(JSON.stringify(result, null, 2))],
  });
};

export const setup = (register: WrappedRegisterToolFuncType) => {
  register(name, config, handler);
};

export default { setup };
